import React from 'react'
import { Box, Card, CardContent, CardMedia, Typography, Button, Stack, IconButton, Tooltip } from '@mui/material'
import { CalendarMonth, LocalOffer, ContentCopy, Check } from '@mui/icons-material'
import { ImagePlaceholder } from './ImagePlaceholder'

interface OfferCardProps {
    title: string
    description: string
    imageUrl: string
    couponCode?: string
    expiryDate: string
    onKnowMore: () => void
    onImageError: () => void
    onCopyCode: () => void
    copied: boolean
    isActive: boolean
    expiresOnText: string
    knowMoreText: string
}

const OfferCard: React.FC<OfferCardProps> = ({
    title,
    description,
    imageUrl,
    couponCode,
    expiryDate,
    onKnowMore,
    onImageError,
    onCopyCode,
    copied,
    isActive,
    expiresOnText,
    knowMoreText
}) => {
    return (
        <Card
            sx={{
                height: '100%',
                width: '100%',
                maxWidth: '100%',
                display: 'flex',
                flexDirection: 'column',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                cursor: 'pointer',
                position: 'relative',
                overflow: 'hidden',
                bgcolor: 'background.paper',
                opacity: isActive ? 1 : 0.7,
                border: '1px solid',
                borderColor: 'borders.default',
                borderRadius: { xs: 1.5, md: 2 },
                '&:hover': {
                    transform: { xs: 'none', md: 'translateY(-8px)' },
                    boxShadow: theme =>
                        `0 12px 24px ${theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.4)' : 'rgba(0,0,0,0.15)'}`
                }
            }}
            onClick={onKnowMore}
        >
            {/* Image Section */}
            <Box
                sx={{
                    position: 'relative',
                    width: '100%',
                    overflow: 'hidden',
                    bgcolor: 'white',
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'center',
                    '&:hover img': {
                        transform: 'scale(1.05)'
                    },
                    '&:hover > div:last-child': {
                        opacity: 1
                    }
                }}
            >
                {imageUrl ? (
                    <CardMedia
                        component="img"
                        image={imageUrl}
                        alt={title}
                        onError={onImageError}
                        sx={{
                            width: '100%',
                            height: 'auto',
                            display: 'block',
                            bgcolor: 'white',
                            transition: 'transform 0.3s ease'
                        }}
                    />
                ) : (
                    <ImagePlaceholder title="No image available" />
                )}
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'linear-gradient(180deg, transparent 0%, rgba(0,0,0,0.7) 100%)',
                        opacity: 0,
                        transition: 'opacity 0.3s ease',
                        pointerEvents: 'none'
                    }}
                />
            </Box>

            {/* Content Section */}
            <CardContent
                sx={{
                    flexGrow: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    p: { xs: 2, sm: 2, md: 2.5 }
                }}
            >
                {/* Title */}
                <Typography
                    variant="h6"
                    sx={{
                        fontWeight: 600,
                        mb: 1,
                        lineHeight: 1.3,
                        fontSize: { xs: '0.95rem', sm: '1rem', md: '1.125rem' },
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        color: 'textColors.primary'
                    }}
                >
                    {title}
                </Typography>

                {/* Description */}
                <Typography
                    variant="body2"
                    sx={{
                        mb: { xs: 1.5, md: 2 },
                        color: 'textColors.secondary',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        lineHeight: 1.5,
                        flexGrow: 1,
                        fontSize: { xs: '0.8125rem', sm: '0.875rem' }
                    }}
                >
                    {description}
                </Typography>

                {/* Coupon Code */}
                {couponCode && (
                    <Box
                        sx={{
                            mb: { xs: 1.5, md: 2 },
                            p: { xs: 0.75, md: 1 },
                            pr: { xs: 0.5, md: 0.75 },
                            borderRadius: 1,
                            border: '1px dashed',
                            borderColor: 'primary.main',
                            bgcolor: 'primary.100',
                            display: 'flex',
                            alignItems: 'center',
                            gap: { xs: 0.5, md: 1 },
                            justifyContent: 'space-between'
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, md: 1 } }}>
                            <LocalOffer sx={{ fontSize: { xs: 14, md: 16 }, color: 'primary.main' }} />
                            <Typography
                                variant="body2"
                                sx={{
                                    fontWeight: 600,
                                    color: 'primary.main',
                                    fontFamily: 'monospace',
                                    letterSpacing: 1,
                                    fontSize: { xs: '0.75rem', md: '0.875rem' }
                                }}
                            >
                                {couponCode}
                            </Typography>
                        </Box>
                        <Tooltip title={copied ? 'Copied!' : 'Copy code'}>
                            <IconButton
                                size="small"
                                onClick={e => {
                                    e.stopPropagation()
                                    onCopyCode()
                                }}
                                sx={{
                                    p: 0.5,
                                    color: 'primary.main',
                                    '&:hover': {
                                        bgcolor: 'primary.200'
                                    }
                                }}
                            >
                                {copied ? <Check sx={{ fontSize: 16 }} /> : <ContentCopy sx={{ fontSize: 16 }} />}
                            </IconButton>
                        </Tooltip>
                    </Box>
                )}

                {/* Expiry Date */}
                <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: { xs: 1.5, md: 2 } }}>
                    <CalendarMonth sx={{ fontSize: { xs: 14, md: 16 }, color: 'textColors.secondary' }} />
                    <Typography
                        variant="caption"
                        sx={{
                            color: 'textColors.secondary',
                            fontSize: { xs: '0.7rem', md: '0.75rem' }
                        }}
                    >
                        {expiresOnText} {expiryDate}
                    </Typography>
                </Stack>

                {/* CTA Button */}
                <Button
                    variant="contained"
                    fullWidth
                    sx={{
                        mt: 'auto',
                        py: { xs: 1, md: 1.25 },
                        borderRadius: 1.5,
                        textTransform: 'none',
                        fontWeight: 600,
                        bgcolor: 'primary.main',
                        fontSize: { xs: '0.8125rem', md: '0.875rem' },
                        '&:hover': {
                            bgcolor: 'primary.dark'
                        }
                    }}
                >
                    {knowMoreText}
                </Button>
            </CardContent>
        </Card>
    )
}

export { OfferCard }
export type { OfferCardProps }
